// Palette + state/classification color maps lifted from the Claude Design
// reference (DOCS/POLICY ENGINE/Policy Review Module.dc.html) so every screen
// in the module renders consistently.

export const PALETTE = {
    teal900: "#004d57",
    teal800: "#0c3a42",
    teal600: "#3a6b78",
    teal500: "#4b8892",
    sage: "#a9c4bb",
    ink: "#15302f",
    body: "#2c3e40",
    muted: "#5f7173",
    faint: "#88a39b",
    border: "#e2e6e4",
    borderSoft: "#eef1ef",
    bg: "#f0f0f0",
    card: "#ffffff",
    panel: "#f6f8f7",
    danger: "#ae232d",
    dangerBg: "#f9d2d3",
    warn: "#9a6a16",
    warnBg: "#fbecd6",
    ok: "#2c7a63",
    okBg: "#dceee7",
};

// Lifecycle state -> [bg, fg], keyed by our backend's snake_case state values.
export const STATE_COLORS: Record<string, [string, string]> = {
    unassigned: ["#ece9e2", "#7a786f"],
    steward_proposed: ["#e7eef0", "#3f7686"],
    steward_ratified: ["#dcebe5", "#1f6b5a"],
    in_review: ["#d9e8ef", "#2f6f86"],
    submitted: ["#cfe3dd", "#00695f"],
    in_collation: ["#fbe6cf", "#b5651d"],
    applied: ["#d7ead9", "#2e7d4f"],
    proposed: ["#e9e3d0", "#8a7320"],
    ratified: ["#004d57", "#ffffff"],
    published: ["#0c3a42", "#a9c4bb"],
};

export const STATE_LABELS: Record<string, string> = {
    unassigned: "Unassigned",
    steward_proposed: "Steward Proposed",
    steward_ratified: "Steward Ratified",
    in_review: "Steward Flagged",
    submitted: "Submitted",
    in_collation: "In Collation",
    applied: "Applied",
    proposed: "Proposed to Board",
    ratified: "Ratified by Board",
    published: "Published",
};

export const LIFECYCLE_ORDER = [
    "unassigned", "steward_proposed", "steward_ratified", "in_review", "submitted",
    "in_collation", "applied", "proposed", "ratified", "published",
];

// Statement classification -> [label, bg, fg]
export const CLASSIFICATION_COLORS: Record<string, [string, string, string]> = {
    PUBLIC_RULE: ["Public rule", "#dceee7", "#2c7a63"],
    INTERNAL_CONTROL: ["Internal control", "#e7eef0", "#3f7686"],
    WORKFLOW_RULE: ["Workflow", "#ece9e2", "#7a786f"],
    DECISION_STANDARD: ["Decision standard", "#e6e9f2", "#4d5aa0"],
    LEGAL_REVIEW_REQUIRED: ["Legal review", "#f9d2d3", "#ae232d"],
    STEWARD_RATIFICATION_REQUIRED: ["Steward ratification", "#dcebe5", "#1f6b5a"],
    SYSTEM_DERIVATION_RULE: ["System derivation", "#dfeaf0", "#2f6f86"],
    NON_AUTOMATABLE: ["Non-automatable", "#fbecd6", "#9a6a16"],
};

// Strip **bold**/*italic* markdown markers for plain-text rendering.
export function strip(s?: string | null): string {
    if (!s) return "";
    return s.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1").trim();
}

// Split "**Lead.** Rest of statement…" into a bold lead-in + remainder,
// matching the design's statement-card typography.
export function parseLead(text: string): { lead: string; rest: string } {
    const m = text.match(/^\s*\*\*(.+?)\*\*\s*([\s\S]*)$/);
    if (m) return { lead: strip(m[1]), rest: strip(m[2]) };
    return { lead: "", rest: strip(text) };
}
